import { kml } from "@tmcw/togeojson";
import JSZip from "jszip";
import area from "@turf/area";
import type { Polygon, MultiPolygon } from "geojson";
import type { Area, Polygon as AppPolygon } from "../models/Area";
import { CuttingQuality } from "../models/Enums";

const ACCEPTED_EXTENSIONS = [".kml", ".kmz", ".geojson", ".json"];
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

export class GeoFileParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "GeoFileParseError";
  }
}

export function isAcceptedFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));
}

export function validateFile(file: File): void {
  if (!isAcceptedFile(file)) {
    throw new GeoFileParseError("Invalid file type. Use " + ACCEPTED_EXTENSIONS.join(", "));
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    throw new GeoFileParseError("File is too large. Maximum size is 10 MB.");
  }
}

interface GeoFeatureWithGeometry {
  type: "Feature";
  geometry: Polygon | MultiPolygon;
  properties?: Record<string, unknown> & { name?: string; description?: string; notes?: string };
}

/**
 * Get display name from feature properties. Supports:
 * - Flat: properties.name
 * - MIAMAPO: properties.miamapo.info.name
 */
function getFeatureName(properties: Record<string, unknown> | undefined, fallback: string): string {
  if (!properties) return fallback;
  const flat = (properties.name as string);
  if (flat && typeof flat === "string") return flat;
  const miamapo = properties.miamapo as Record<string, unknown> | undefined;
  const info = miamapo?.info as Record<string, unknown> | undefined;
  const name = info?.name as string | undefined;
  if (name && typeof name === "string") return name;
  return fallback;
}

/**
 * Get notes/description from feature properties. Supports:
 * - Flat: properties.notes, properties.description
 * - MIAMAPO: properties.miamapo.info.description
 */
function getFeatureNotes(properties: Record<string, unknown> | undefined): string {
  if (!properties) return "";
  const flat = (properties.notes as string) || (properties.description as string);
  if (flat && typeof flat === "string") return flat;
  const miamapo = properties.miamapo as Record<string, unknown> | undefined;
  const info = miamapo?.info as Record<string, unknown> | undefined;
  const desc = info?.description as string | undefined;
  if (desc && typeof desc === "string") return desc;
  return "";
}

function getNumberOrFallback(properties: Record<string, unknown> | undefined, key: string, fallback: number): number {
  if (!properties) return fallback;
  const val = properties[key];
  if (typeof val === "number") return val;
  if (typeof val === "string") {
    const n = parseFloat(val);
    if (!isNaN(n)) return n;
  }
  return fallback;
}

function getCuttingQualityOrFallback(properties: Record<string, unknown> | undefined): CuttingQuality {
  if (!properties) return CuttingQuality.Regular;
  const val = (properties.cuttingQuality as string)?.toLowerCase();
  if (val === "pro") return CuttingQuality.Pro;
  if (val === "prosport") return CuttingQuality.ProSport;
  return CuttingQuality.Regular;
}

function getPolygonFeaturesFromGeoJSON(geojson: unknown): GeoFeatureWithGeometry[] {
  const features: GeoFeatureWithGeometry[] = [];

  if (!geojson || typeof geojson !== "object") {
    return features;
  }

  const obj = geojson as { type?: string; features?: unknown[]; geometry?: unknown };
  if (obj.type === "FeatureCollection" && Array.isArray(obj.features)) {
    for (const f of obj.features) {
      const feat = f as GeoFeatureWithGeometry;
      if (feat?.type === "Feature" && feat.geometry) {
        if (feat.geometry.type === "Polygon") {
          features.push(feat);
        } else if (feat.geometry.type === "MultiPolygon") {
          for (const coords of feat.geometry.coordinates) {
            features.push({
              ...feat,
              geometry: { type: "Polygon", coordinates: coords },
            });
          }
        }
      }
    }
  } else if (obj.type === "Feature" && obj.geometry) {
    const feat = obj as GeoFeatureWithGeometry;
    if (feat.geometry.type === "Polygon") {
      features.push(feat);
    } else if (feat.geometry.type === "MultiPolygon") {
      for (const coords of feat.geometry.coordinates) {
        features.push({
          ...feat,
          geometry: { type: "Polygon", coordinates: coords },
        });
      }
    }
  }

  return features;
}

async function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file, "utf-8");
  });
}

async function parseKmz(file: File): Promise<GeoFeatureWithGeometry[]> {
  const arrayBuffer = await file.arrayBuffer();
  const zip = await JSZip.loadAsync(arrayBuffer);
  const kmlEntry =
    zip.file("doc.kml") ?? Object.values(zip.files).find((f) => f.name.toLowerCase().endsWith(".kml"));
  if (!kmlEntry) {
    throw new GeoFileParseError("KMZ file does not contain a .kml file.");
  }
  const kmlText = await kmlEntry.async("text");
  return parseKmlString(kmlText);
}

function parseKmlString(xmlText: string): GeoFeatureWithGeometry[] {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlText, "text/xml");
  const geojson = kml(doc);
  return getPolygonFeaturesFromGeoJSON(geojson);
}

async function parseGeojsonFile(file: File): Promise<GeoFeatureWithGeometry[]> {
  const text = await readFileAsText(file);
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    throw new GeoFileParseError("Invalid GeoJSON: file is not valid JSON.");
  }
  return getPolygonFeaturesFromGeoJSON(data);
}

/**
 * Parse a .kml, .kmz or .geojson file and return polygon features (one per polygon, MultiPolygon split into multiple).
 */
export async function parseGeoFile(file: File): Promise<GeoFeatureWithGeometry[]> {
  validateFile(file);
  const name = file.name.toLowerCase();

  if (name.endsWith(".kmz")) {
    return parseKmz(file);
  }
  if (name.endsWith(".kml")) {
    const text = await readFileAsText(file);
    return parseKmlString(text);
  }
  if (name.endsWith(".geojson") || name.endsWith(".json")) {
    return parseGeojsonFile(file);
  }

  throw new GeoFileParseError("Unsupported file format.");
}

/**
 * Convert GeoJSON polygon features to Area[] for SaveAreasModel.
 * Uses WGS84; area is computed with @turf/area (m²).
 */
export function geoFeaturesToAreas(features: GeoFeatureWithGeometry[]): Area[] {
  return features.map((feat, index) => {
    const id = crypto.randomUUID();
    const name = getFeatureName(feat.properties, `Area ${index + 1}`);
    const notes = getFeatureNotes(feat.properties);
    const geom = feat.geometry as Polygon;
    const polygonData: AppPolygon = {
      type: "Polygon",
      coordinates: geom.coordinates,
    };
    const sizeM2 = area({
      type: "Feature",
      geometry: geom,
      properties: {},
    });
    const size = Math.round(sizeM2 * 100) / 100; // two decimals

    const areaObj: Area = {
      id,
      name,
      size,
      maxSlope: getNumberOrFallback(feat.properties, "maxSlope", 0),
      availableHoursToCutPerDay: getNumberOrFallback(feat.properties, "availableHoursToCutPerDay", 24),
      cuttingQuality: getCuttingQualityOrFallback(feat.properties),
      coveragePercent: getNumberOrFallback(feat.properties, "complexityPercentage", 0),
      proposalProductConnections: [],
      polygonData,
      notes,
    };
    return areaObj;
  });
}
